import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, Teleport as _Teleport, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3d81bf21"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "select-autocomplete" }
const _hoisted_2 = ["id", "aria-labelledby", "aria-activedescendant"]
const _hoisted_3 = /*#__PURE__*/_createTextVNode("Loading results")
const _hoisted_4 = /*#__PURE__*/_createTextVNode("Error loading results")
const _hoisted_5 = ["id", "data-highlighted", "onClick", "onMouseenterCapture"]
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 1,
  class: "option-info truncate"
}
const _hoisted_8 = {
  key: 0,
  class: "description"
}

import { ref, watch, nextTick, computed } from "vue";
import { uniqueId } from "lodash";
import { OptionsFunc } from "./AppSelectAutocomplete";
import { wrap } from "@/util/math";
import { useFloating, useQuery } from "@/util/composables";
import AppTextbox from "./AppTextbox.vue";

interface Props {
  /** two-way bound search state */
  modelValue?: string;
  /** name of the field */
  name: string;
  /** placeholder string when nothing typed in */
  placeholder?: string;
  /** async function that returns list of options to show */
  options: OptionsFunc;
  /** description to show below box */
  description?: string;
}

interface Emits {
  /** two-way bound search state */
  (event: "update:modelValue", value: string): void;
  /** when input focused */
  (event: "focus"): void;
  /** when input value change "submitted"/"committed" by user */
  (event: "change", value: string): void;
  /** when user wants to delete an entry */
  (event: "delete", value: string): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppSelectAutocomplete',
  props: {
    modelValue: null,
    name: null,
    placeholder: null,
    options: null,
    description: null
  },
  emits: ["update:modelValue", "focus", "change", "delete"],
  setup(__props: any, { emit }: { emit: ({
  /** two-way bound search state */
  (event: "update:modelValue", value: string): void;
  /** when input focused */
  (event: "focus"): void;
  /** when input value change "submitted"/"committed" by user */
  (event: "change", value: string): void;
  /** when user wants to delete an entry */
  (event: "delete", value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /** two-way bound search state */
  modelValue?: string;
  /** name of the field */
  name: string;
  /** placeholder string when nothing typed in */
  placeholder?: string;
  /** async function that returns list of options to show */
  options: OptionsFunc;
  /** description to show below box */
  description?: string;
}





/** unique id for instance of component */
const id = ref(uniqueId());
/** currently searched text */
const search = ref("");
/** index of option that is highlighted */
const highlighted = ref(0);
/** whether input box focused and dropdown expanded */
const expanded = ref(false);

/** open results dropdown */
async function open() {
  expanded.value = true;
  highlighted.value = -1;
  await getResults();
}

/** close results dropdown */
function close() {
  expanded.value = false;
  highlighted.value = -1;
  results.value = [];
}

/** when user focuses box */
function onFocus() {
  emit("focus");
  open();
}

/** when user blurs box */
async function onBlur() {
  close();
}

/** when user types some text, after a delay */
async function onDebounce() {
  await getResults();
}

/** when user "commits" change to value, e.g. pressing enter, de-focusing, etc */
function onChange(value: string) {
  select(value);
}

/** when user presses key in input */
async function onKeydown(event: KeyboardEvent) {
  /** reopen if previously submitted */
  expanded.value = true;

  /** arrow/home/end keys */
  if (["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
    /** prevent page scroll */
    event.preventDefault();

    /** move value up/down */
    let index = highlighted.value;
    if (event.key === "ArrowUp") index--;
    if (event.key === "ArrowDown") index++;
    if (event.key === "Home") index = 0;
    if (event.key === "End") index = results.value.length - 1;

    /** update highlighted, wrapping beyond 0 or results length */
    highlighted.value = wrap(index, 0, results.value.length - 1);
  }

  /** enter key to select highlighted result */
  if (event.key === "Enter" && highlighted.value >= 0) {
    event.stopPropagation();
    select(results.value[highlighted.value].name);
  }

  /** delete key to delete the highlighted result */
  if (event.key === "Delete" && event.shiftKey) {
    emit("delete", results.value[highlighted.value].name);
    await getResults();
  }

  /** esc key to close dropdown */
  if (event.key === "Escape") close();
}

/** select an option */
async function select(value: string) {
  search.value = value;
  emit("change", value);
  close();
}

const {
  query: getResults,
  data: results,
  isLoading,
  isError,
} = useQuery(
  /** get list of results */
  async function () {
    /** get results */
    return await props.options(search.value);
  },

  /** default value */
  []
);

/** target element */
const target = ref();
/** dropdown element */
const dropdown = ref();
/** get dropdown position */
const { calculate, style } = useFloating(
  computed(() => target.value?.textbox),
  dropdown,
  true
);
/** recompute position when length of results changes */
watch([expanded, results], async () => {
  await nextTick();
  if (expanded.value) calculate();
});

/** when model changes, update search */
watch(
  () => props.modelValue,
  () => (search.value = props.modelValue || ""),
  { immediate: true }
);

/** when search changes, update model */
watch(search, () => {
  emit("update:modelValue", search.value);
});

/** when highlighted index changes */
watch(highlighted, () => {
  /** scroll to highlighted in dropdown */
  document
    .querySelector(`#option-${id.value}-${highlighted.value} > *`)
    ?.scrollIntoView({ block: "nearest" });
});

return (_ctx: any,_cache: any) => {
  const _component_AppStatus = _resolveComponent("AppStatus")!
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AppTextbox, {
      id: `select-${id.value}`,
      ref_key: "target",
      ref: target,
      modelValue: search.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
      class: "box",
      icon: "search",
      role: "combobox",
      placeholder: __props.placeholder,
      debounce: 200,
      "aria-label": __props.name,
      "aria-expanded": !!_unref(results).length,
      "aria-controls": `list-${id.value}`,
      "aria-haspopup": "listbox",
      "aria-autocomplete": "list",
      onFocus: onFocus,
      onBlur: onBlur,
      onDebounce: onDebounce,
      onChange: onChange,
      onKeydown: onKeydown
    }, null, 8, ["id", "modelValue", "placeholder", "aria-label", "aria-expanded", "aria-controls"]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (expanded.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            id: `list-${id.value}`,
            ref_key: "dropdown",
            ref: dropdown,
            class: "list",
            role: "listbox",
            tabindex: "0",
            "aria-labelledby": `select-${id.value}`,
            "aria-activedescendant": 
          _unref(results).length && highlighted.value >= 0
            ? `option-${id.value}-${highlighted.value}`
            : undefined
        ,
            style: _normalizeStyle(_unref(style))
          }, [
            (_unref(isLoading))
              ? (_openBlock(), _createBlock(_component_AppStatus, {
                  key: 0,
                  code: "loading",
                  role: "option"
                }, {
                  default: _withCtx(() => [
                    _hoisted_3
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_unref(isError))
              ? (_openBlock(), _createBlock(_component_AppStatus, {
                  key: 1,
                  code: "error",
                  role: "option"
                }, {
                  default: _withCtx(() => [
                    _hoisted_4
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(results), (option, index) => {
              return _withDirectives((_openBlock(), _createElementBlock("div", {
                id: `option-${id.value}-${index}`,
                key: index,
                class: "option",
                role: "option",
                "aria-selected": true,
                "data-highlighted": index === highlighted.value,
                tabindex: "0",
                onClick: _withModifiers(() => select(option.name), ["prevent"]),
                onMouseenterCapture: ($event: any) => (highlighted.value = index),
                onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
                onFocusin: _cache[2] || (_cache[2] = () => null),
                onKeydown: _cache[3] || (_cache[3] = () => null)
              }, [
                (option.icon)
                  ? (_openBlock(), _createBlock(_component_AppIcon, {
                      key: 0,
                      icon: option.icon,
                      class: "option-icon"
                    }, null, 8, ["icon"]))
                  : _createCommentVNode("", true),
                _createElementVNode("span", {
                  class: "option-label truncate",
                  innerHTML: option.highlight || option.name
                }, null, 8, _hoisted_6),
                (option.info)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(option.info), 1))
                  : _createCommentVNode("", true)
              ], 40, _hoisted_5)), [
                [_directive_tooltip, option.tooltip]
              ])
            }), 128))
          ], 12, _hoisted_2))
        : _createCommentVNode("", true)
    ])),
    (__props.description)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(__props.description), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})